import React, { useState, useEffect, useMemo } from 'react';
import { Card, Input, Button, Space, message, Spin, Table } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { CategoryService } from '@/services/category.service';
import { TournamentService } from '@/services/tournament.service';
import type { Tournament } from '@/types/api.types';
import type { Category } from '@/types/models/tournament';
import { logger } from '@/utils/logger';
import CreateCategoryModal from '@/components/club/CreateCategoryModal';
import { AddCategoryToTournamentModal } from './AddCategoryToTournamentModal';

interface TournamentCategoriesEditorProps {
  tournament: Tournament | null;
  onSuccess?: () => void;
}

const genderLabels: Record<string, string> = {
  male: 'М',
  female: 'Ж',
  mixed: 'М/Ж',
};

export const TournamentCategoriesEditor: React.FC<TournamentCategoriesEditorProps> = ({
  tournament,
  onSuccess,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [createCategoryModalOpen, setCreateCategoryModalOpen] = useState(false);

  // В таблице показываем только выбранные категории (с опциональным поиском по ним)
  const selectedCategoryObjects = useMemo(() => {
    const list = selectedCategories
      .map((id) => categories.find((c) => c.id === id))
      .filter(Boolean) as Category[];
    if (!searchText.trim()) return list;
    const q = searchText.toLowerCase().trim();
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        `${c.ageFrom}-${c.ageTo}`.includes(q) ||
        `${c.weightFrom}-${c.weightTo}`.includes(q) ||
        (c.gender === 'male' && q.includes('муж')) ||
        (c.gender === 'female' && q.includes('жен'))
    );
  }, [categories, selectedCategories, searchText]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await CategoryService.getAll();
      setCategories(data);
    } catch (error) {
      logger.error('Failed to fetch categories:', error);
      message.error('Не удалось загрузить категории');
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (tournament?.tournamentCategories?.length) {
      const ids = tournament.tournamentCategories
        .map((tc) => (tc as any).weightCategory?.id)
        .filter(Boolean) as number[];
      setSelectedCategories(ids);
    } else {
      setSelectedCategories([]);
    }
  }, [tournament?.id, tournament?.tournamentCategories]);

  const handleSave = async () => {
    if (!tournament?.id) return;
    try {
      setSaving(true);
      const categoryData = selectedCategories.map((categoryId) => ({
        tournamentId: tournament.id,
        categoryId,
        status: 'active' as const,
      }));
      await TournamentService.updateCategories(tournament.id, categoryData);
      message.success('Категории турнира сохранены');
      onSuccess?.();
    } catch (error) {
      logger.error('Failed to save categories:', error);
      message.error('Не удалось сохранить категории');
    } finally {
      setSaving(false);
    }
  };

  const removeFromTournament = (categoryId: number) => {
    setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
  };

  const handleAddCategories = (idsToAdd: number[]) => {
    setSelectedCategories((prev) => [...prev, ...idsToAdd]);
  };

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      ellipsis: true,
      sorter: (a: Category, b: Category) => a.name.localeCompare(b.name),
    },
    {
      title: 'Пол',
      dataIndex: 'gender',
      key: 'gender',
      width: 56,
      align: 'center' as const,
      render: (gender: string) => genderLabels[gender] ?? gender,
      sorter: (a: Category, b: Category) => (a.gender || '').localeCompare(b.gender || ''),
    },
    {
      title: 'Возраст от',
      dataIndex: 'ageFrom',
      key: 'ageFrom',
      width: 90,
      align: 'right' as const,
      sorter: (a: Category, b: Category) => a.ageFrom - b.ageFrom,
    },
    {
      title: 'Возраст до',
      dataIndex: 'ageTo',
      key: 'ageTo',
      width: 90,
      align: 'right' as const,
      sorter: (a: Category, b: Category) => a.ageTo - b.ageTo,
    },
    {
      title: 'Вес от',
      dataIndex: 'weightFrom',
      key: 'weightFrom',
      width: 80,
      align: 'right' as const,
      render: (v: number) => (v === 0 ? '—' : v),
      sorter: (a: Category, b: Category) => a.weightFrom - b.weightFrom,
    },
    {
      title: 'Вес до',
      dataIndex: 'weightTo',
      key: 'weightTo',
      width: 80,
      align: 'right' as const,
      render: (v: number) => (v === 0 ? '—' : v),
      sorter: (a: Category, b: Category) => a.weightTo - b.weightTo,
    },
    {
      title: 'Пояса',
      dataIndex: 'beltLevels',
      key: 'beltLevels',
      width: '18%',
      ellipsis: true,
      render: (arr: string[] | undefined) =>
        Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: Category) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeFromTournament(record.id)}
        >
          Удалить
        </Button>
      ),
    },
  ];

  if (!tournament) {
    return <div>Загрузка турнира...</div>;
  }

  return (
    <Card
      title="Категории турнира"
      extra={
        <Button type="primary" onClick={handleSave} loading={saving}>
          Сохранить категории
        </Button>
      }
    >
      <p style={{ color: '#8c8c8c', marginBottom: 16 }}>
        В таблице отображаются только категории, добавленные в турнир. Добавьте категории кнопкой «Добавить категорию», создайте новую — кнопкой «Создать категорию». Не забудьте нажать «Сохранить категории».
      </p>

      {loadingCategories ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap align="center">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddModalOpen(true)}
              >
                Добавить категорию
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setCreateCategoryModalOpen(true)}
              >
                Создать категорию
              </Button>
              <Input
                placeholder="Поиск по выбранным..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ width: 220 }}
              />
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                В турнире: {selectedCategories.length}
              </span>
            </Space>

            <Table<Category>
              rowKey="id"
              size="small"
              bordered
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Всего: ${total}`,
                pageSizeOptions: ['20', '50', '100'],
              }}
              columns={columns}
              dataSource={selectedCategoryObjects}
              scroll={{ x: 700 }}
              locale={{
                emptyText: selectedCategories.length === 0
                  ? 'Нет категорий в турнире. Нажмите «Добавить категорию».'
                  : searchText
                    ? 'Ничего не найдено'
                    : 'Нет категорий',
              }}
            />
          </Space>

          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleSave} loading={saving}>
              Сохранить категории
            </Button>
          </div>
        </>
      )}

      <AddCategoryToTournamentModal
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onAdd={handleAddCategories}
        allCategories={categories}
        alreadySelectedIds={selectedCategories}
      />

      <CreateCategoryModal
        visible={createCategoryModalOpen}
        onCancel={() => setCreateCategoryModalOpen(false)}
        onSuccess={() => {
          setCreateCategoryModalOpen(false);
          fetchCategories();
        }}
      />
    </Card>
  );
};
